import { createElementSize } from "@solid-primitives/resize-observer";
import { type Component, type JSXElement, type ParentComponent, Show, createSignal } from "solid-js";
import { Motion, Presence } from "@repo/solid-motionone";
import { Icon, IconName, IconsSize } from "@core/components/icons";
import { usePromptContext } from "../PromptContext";
import { ChatScope } from "./ChatScope";
import { useWire } from "@core/wire";
import { ExtensionChatScope } from "./ExtensionChatScope";
import { useIsUserGettingStarted } from "@core/lib/useIsUserGettingStarted";
import { Spinner } from "@core/components/_original/Spinner";
import { Tooltip, TooltipTrigger } from "@core/components/_original/Tooltip";
export const ChatTopBar: Component = () => {
  const wire = useWire();
  const { focused, showQuestionLibrary, rawProps } = usePromptContext();
  const [ref, setRef] = createSignal<HTMLDivElement>();
  const size = createElementSize(ref);
  const { isUserGettingStarted } = useIsUserGettingStarted();
  return (
    <Presence exitBeforeEnter>
      <Show when={focused() && (!isUserGettingStarted() || !wire.isApp())}>
        <Motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: 1,
            height: showQuestionLibrary() ? `${size.height}px` : "2.375rem",
          }}
          exit={{ opacity: 0, height: 0 }}
          class="relative overflow-y-hidden scrollbar-hide"
        >
          <div
            ref={setRef}
            class="absolute top-0 left-0 right-0 text-2xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-slate-900 rounded-t"
          >
            <div class="flex items-center justify-between gap-2 h-9 px-2">
              <Show when={rawProps.processingAssets?.()} fallback={<div aria-hidden />}>
                <Tooltip
                  placement="top"
                  content={<div class="max-w-80">Files still processing - answer may be incomplete.</div>}
                  theme="default"
                >
                  <TooltipTrigger class="inline-flex items-center gap-2 w-auto select-none cursor-pointer" as="div">
                    <Spinner class="size-4" />
                    <span class="text-on-surface text-sm">
                      {rawProps.processingAssets?.() === 1
                        ? "1 file is processing"
                        : `${rawProps.processingAssets?.()} files are processing`}
                    </span>
                  </TooltipTrigger>
                </Tooltip>
              </Show>
              <Show when={wire.isApp()} fallback={<ExtensionChatScope />}>
                <Show when={!isUserGettingStarted()}>
                  <ChatScope />
                </Show>
              </Show>
            </div>
          </div>
        </Motion.div>
      </Show>
    </Presence>
  );
};

const ToolbarButton: ParentComponent<{
  onClick?: () => void;
  active?: boolean;
  icon?: JSXElement;
  id?: string;
  testId?: string;
}> = (props) => (
  <button
    id={props.id}
    data-testid={props.testId}
    type="button"
    onClick={props.onClick}
    class="min-w-max min-h-max w-auto h-auto px-1 rounded-md flex items-center gap-1 border border-transparent"
    classList={{
      "bg-gray-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-gray-300 dark:hover:bg-slate-700":
        !props.active,
      "bg-blue-200 dark:bg-blue-900 text-blue-600 dark:text-blue-100 border border-blue-700": props.active,
    }}
  >
    {props.icon}
    {props.children}
  </button>
);

const LLMSelector = () => (
  <div class="flex items-center gap-2">
    <div class="mr-1">Chat powered by</div>

    <ToolbarButton active>Claude-3.0 Haiku</ToolbarButton>
    <ToolbarButton>GPT-3.5 Turbo</ToolbarButton>
  </div>
);
